import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { antlrToLezer } from '@gonfalon/dataviz-experimental/QueryBuilder';
import { FieldGroup } from '@launchpad-ui/components';

import type { MetricForm } from '../formUtils';
import { MetricSourceQueryBuilder } from '../MetricSourceQueryBuilder';

import { RequiredLabel } from './RequiredLabel';

import styles from './TraceField.module.css';

interface TraceFieldProps {
  context: UseFormReturn<MetricForm>;
}

const FIELD_NAME = 'traceQuery';

export const TraceField: React.FC<TraceFieldProps> = ({ context }) => {
  const {
    formState: { errors },
  } = context;
  // Captured once at mount. MetricSourceQueryBuilder owns its internal query state
  // and syncs back to the form via setValue. Passing the live field.value would
  // cause key={lezerDefault} inside MetricSourceQueryBuilder to remount QueryBuilder
  // on every update, discarding parsed tokens back to free text.
  //
  // The form field stores ANTLR; the QueryBuilder consumes Lezer, so convert at
  // this boundary (onChange writes ANTLR back). Seeding the field with ANTLR is
  // what keeps an unedited save correct — the value goes to the API verbatim.
  const [defaultValue] = useState(() => {
    const antlr = context.getValues(FIELD_NAME) as string | undefined;
    return antlr ? antlrToLezer(antlr) : undefined;
  });

  return (
    <Controller
      control={context.control}
      name={FIELD_NAME}
      rules={{ required: 'Span query is required' }}
      shouldUnregister
      render={({ field }) => (
        <MetricSourceQueryBuilder
          defaultValue={defaultValue}
          inputId={FIELD_NAME}
          onChange={field.onChange}
          wrapFilters={(filters) => (
            <FieldGroup errorMessage={errors.traceQuery?.message} className={styles.traceField}>
              <RequiredLabel label="Filter to spans" htmlFor={FIELD_NAME} />
              {filters}
            </FieldGroup>
          )}
        />
      )}
    />
  );
};
